import { useTranslations } from 'next-intl';
import { JobSummaryFragment } from '../gen/graphql/JobRoute.generated';
import { useAddressFormatter } from '@/hooks/useAddressFormatter';
import { useJobCategoryFormatter } from '@/hooks/useJobCategoryFormatter';
import { useI18nLabel } from '@/hooks/useI18nLabel';
import { AppDownloadBanner } from '@/components/common/AppDownloadBanner/AppDownloadBanner';
import { Tag } from '@/components/common/Tag/Tag';
import Image from 'next/image';
import { Callout } from '@/components/common/Callout/Callout';
import { currencySymbolForJPY } from '@/utils/number';
import { formatJPY } from '@/utils/number';
import { ScheduleIcon } from '@/components/icons/gen/ScheduleIcon';
import { StoreIcon } from '@/components/icons/gen/StoreIcon';
import { LocationIcon } from '@/components/icons/gen/LocationIcon';
import { FlagIcon } from '@/components/icons/gen/FlagIcon';
import { LightbulbIcon } from '@/components/icons/gen/LightbulbIcon';
import { formatMinutesToHours } from '@/utils/duration';
import { makeWorkplaceDetailDeeplink } from '@/utils/deeplink';

export interface JobSummaryProps {
  jobSummary: JobSummaryFragment;
}

export function JobSummary({ jobSummary }: JobSummaryProps) {
  const t = useTranslations();
  const { formatAddress } = useAddressFormatter();
  const { formatJobCategory } = useJobCategoryFormatter();
  const translate = useI18nLabel();

  const {
    title,
    mainPhoto,
    rangeOfHourlyWage,
    benefits,
    rangeOfWorkingTime,
    address,
    category,
    workplace,
    access,
  } = jobSummary;

  // Format schedule text based on rangeOfWorkingTime
  const scheduleText = rangeOfWorkingTime
    ? rangeOfWorkingTime.min === rangeOfWorkingTime.max
      ? t('job.jobSummary.workingHours', {
          num: formatMinutesToHours(rangeOfWorkingTime.min ?? 0),
        })
      : t('job.jobSummary.workingHoursWithRange', {
          min: formatMinutesToHours(rangeOfWorkingTime.min ?? 0),
          max: formatMinutesToHours(rangeOfWorkingTime.max ?? 0),
        })
    : '';

  // Format full address
  const workplaceAddress = formatAddress(address);

  // Format category text
  const categoryText = formatJobCategory(category);
  return (
    <div className="flex sp:flex-col tab:flex-row pc:flex-row gap-4">
      <div className="flex flex-col gap-4 flex-1 min-w-[0px]">
        <Callout Icon={LightbulbIcon}>{t('job.note')}</Callout>
        <p className="text-neutral-primary text-x2-large font-bold [overflow-wrap:anywhere]">
          {title}
        </p>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-0.5 items-baseline text-brand-primary">
            {rangeOfHourlyWage ? (
              rangeOfHourlyWage.min === rangeOfHourlyWage.max ? (
                <div className="font-bold font-numeric flex items-baseline">
                  <div className="text-style-num-small">{currencySymbolForJPY()}</div>
                  <div className="text-style-num-x-large">
                    {formatJPY(rangeOfHourlyWage?.min || 0)}
                  </div>
                </div>
              ) : (
                <>
                  <div className="font-bold flex items-baseline">
                    <div className="text-style-num-small">{currencySymbolForJPY()}</div>
                    <div className="text-style-num-large font-numeric">
                      {formatJPY(rangeOfHourlyWage?.min || 0)}
                    </div>
                  </div>
                  <div className="font-bold text-medium">{t('job.jobSummary.rangeDelimiter')}</div>
                  <div className="font-bold flex items-baseline">
                    <div className="text-style-num-small">{currencySymbolForJPY()}</div>
                    <div className="text-style-num-large">
                      {formatJPY(rangeOfHourlyWage?.max || 0)}
                    </div>
                  </div>
                </>
              )
            ) : (
              <span>-</span>
            )}
          </div>
          {/* Job Information Section */}
          <div className="flex flex-col gap-1">
            {/* Schedule Row */}
            {scheduleText && (
              <div className="flex items-center gap-1">
                <ScheduleIcon className="size-4 text-neutral-primary flex-shrink-0" />
                <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">
                  {scheduleText}
                </p>
              </div>
            )}

            {/* Workplace Name Row */}
            <div className="flex items-center gap-1">
              <StoreIcon className="size-4 text-neutral-primary flex-shrink-0" />
              <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">
                {workplace.name}
              </p>
            </div>

            {/* Location Row */}
            <div className="flex items-center gap-1">
              <LocationIcon className="size-4 text-neutral-primary flex-shrink-0" />
              <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">
                {workplaceAddress}
              </p>
            </div>

            {/* Access Info Row */}
            {access && (
              <div className="flex items-center gap-1">
                <FlagIcon className="size-4 text-neutral-primary flex-shrink-0" />
                <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">
                  {access}
                </p>
              </div>
            )}
          </div>

          {/* Tag & Category Section */}
          <div className="flex flex-col gap-1">
            {/* Category */}
            <div className="w-full">
              <span className="text-minute text-neutral-secondary">{categoryText}</span>
            </div>

            {/* Tags/Benefits */}
            <div className="flex flex-wrap gap-1">
              {benefits?.map((benefit) => (
                <Tag colorScheme="gray" size="small" key={benefit.id} label={translate(benefit)} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 w-full tab:max-w-[336px] pc:max-w-[336px]">
        <div className="relative aspect-[3/2] rounded-small overflow-hidden">
          <Image src={mainPhoto.imageURL} alt={title} fill />
        </div>
        <AppDownloadBanner
          href={makeWorkplaceDetailDeeplink(jobSummary.workplace.id)}
          className="hidden tab:flex pc:flex"
        />
      </div>
    </div>
  );
}
